import { LIMING_TYPE, LIMING_UNIT, LimingEvent } from '@cibo/core'
import { DatePickerField, NumberInputField, SelectField } from '@cibo/ui'
import ClearIcon from '@mui/icons-material/Clear'
import EditRounded from '@mui/icons-material/EditRounded'
import {
  Button,
  Collapse,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { Formik, FormikHelpers, useField } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { EventComponentProps } from '../EventDetail/EventDetailTypes'
import { useIsRequired } from '../useIsRequired'

const LimingEventValidationSchema = ({ t }: any) =>
  Yup.object().shape({
    date: Yup.date(),
    type: Yup.string(),
    amount: Yup.number()
      .required(t('required', { context: 'amount' }))
      .min(0),
    unit: Yup.string().required(t('required', { context: 'unit' })),
  })

export const LimingEventEditorSimple = ({
  editing,
  year,
  name,
  onEditEvent,
  onPressRemove,
  requirement,
  isImmutable,
}: EventComponentProps) => {
  const { t } = useTranslation('@cibo/landmanager/LimingEditor')
  const [{ value }, { initialValue, touched }, { setValue, setTouched }] =
    useField<LimingEvent>(name)
  const [localEditing, setLocalEditing] = useState(!initialValue)
  const isRequired = useIsRequired(requirement)

  useEffect(() => {
    onEditEvent(!initialValue)
  }, [!initialValue])

  const defaultMonth = new Date(year, 9)
  const validationSchema = LimingEventValidationSchema({ t })

  const handleSubmit = (values: LimingEvent, { setSubmitting }: FormikHelpers<LimingEvent>) => {
    setTouched(true, false)
    setValue(values)
    setLocalEditing(false)
    setSubmitting(false)
    onEditEvent(false)
  }

  const editingDisabled = editing || isImmutable

  return (
    <>
      <Collapse in={!localEditing}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: '275px',
          }}
        >
          <Stack spacing={1}>
            <Typography>{value.type ? t(value.type) : '--'}</Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              divider={<Divider orientation="vertical" flexItem />}
            >
              {value.date && (
                <Typography variant="caption">
                  {t('eventDate', { date: new Date(value.date) })}
                </Typography>
              )}
              {value.amount !== undefined && (
                <Typography variant="caption">
                  {value.amount} {value.unit && t(value.unit)}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack direction="column">
            <Tooltip title={t('edit')}>
              <IconButton
                aria-label={t('editEvent') as string}
                onClick={() => {
                  setLocalEditing(true)
                  onEditEvent(true)
                }}
                size="small"
                disabled={editingDisabled}
              >
                <EditRounded />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('remove')}>
              <IconButton
                aria-label={t('removeEvent') as string}
                onClick={onPressRemove}
                size="small"
                disabled={editingDisabled}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Collapse>
      <Formik
        initialValues={
          value || ({ date: null, type: '', amount: 0, unit: '' } as unknown as LimingEvent)
        }
        validationSchema={validationSchema}
        validateOnMount
        enableReinitialize
        disabled={isImmutable}
        onSubmit={handleSubmit}
      >
        {({ isValid, resetForm, submitForm, dirty }) => {
          return (
            <Collapse in={localEditing} timeout="auto" unmountOnExit>
              <Stack spacing={2}>
                <FormControl fullWidth required={isRequired('date')}>
                  <FormLabel>{t('date')}</FormLabel>
                  <DatePickerField
                    name={`date`}
                    minDate={new Date(year - 1, 6, 1)}
                    maxDate={new Date(year + 1, 0, 1)}
                    referenceDate={defaultMonth}
                    data-testid={`date`}
                  />
                </FormControl>

                <FormControl fullWidth required={isRequired('type')}>
                  <FormLabel>{t('type')}</FormLabel>
                  <SelectField fieldName="type" renderOption={t} options={LIMING_TYPE} />
                </FormControl>

                <FormControl fullWidth required={isRequired('amount')}>
                  <FormLabel>{t('amount')}</FormLabel>
                  <NumberInputField name={`amount`} data-testid={`amount`} min={0} />
                </FormControl>

                <FormControl fullWidth required={isRequired('unit')}>
                  <FormLabel>{t('unit')}</FormLabel>
                  <SelectField fieldName="unit" renderOption={t} options={LIMING_UNIT} />
                </FormControl>

                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    data-testid={`${name}-cancel`}
                    onClick={() => {
                      !initialValue && !touched && onPressRemove()
                      setLocalEditing(false)
                      onEditEvent(false)
                      resetForm()
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    data-testid={`${name}-add`}
                    size="small"
                    onClick={() => submitForm()}
                    disabled={!isValid || !dirty}
                  >
                    {!initialValue ? t('add') : t('saveEvent')}
                  </Button>
                </Stack>
              </Stack>
            </Collapse>
          )
        }}
      </Formik>
    </>
  )
}
